<template lang="pug">
  communications-layout
    //- Displays the Error Snackbar on the bottom of the page
    v-snackbar(:value="conversationsError" color="error")
      div {{ conversationsErrorMessage }}

    //- Displays the Success Snackbar on the bottom of the page
    v-snackbar(:value="conversationsSuccess" color="success")
      div {{ conversationsSuccessMessage }}

    //- Displays the Error Snackbar on the bottom of the page
    v-snackbar(:value="templatesError" color="error")
      div {{ templatesErrorMessage }}

    //- Displays the Success Snackbar on the bottom of the page
    v-snackbar(:value="templatesSuccess" color="success")
      div {{ templatesSuccessMessage }}

    processing-action-modal(
      v-if="sendingTextInProgress"
      :completed="textsSent"
      :total="toSelected.length"
    )

    //- Dialog to prompt confirmation on clearing the fields
    liondesk-dialog(
      dialogTitle="Clear All Fields"
      dialogBody="Are you sure you want to clear the selected recipients and body?"
      v-model="clearConfirmationDialog"
      @click:cancel="clearConfirmationDialog = false"
      @click:ok="clearAll"
    )

    //- The confirm leave modal for the user if they have data
    liondesk-dialog(
      :value="confirmLeaveModal_isVisible"
      dialogTitle="Unsaved changes"
      dialogBody="Leaving this page will clear all changes, are you sure?"
      okButtonText="Yes"
      cancelButtonText="No"
      @click:cancel="confirmLeaveModal_cancel"
      @click:ok="confirmLeaveModal_confirm"
    )

    v-layout.column
      v-flex.py-0(xs12)
        v-flex.py-0(xs12)
          v-flex(x12)
            a.trailback(@click="handleGoBack")
              v-icon(small) keyboard_arrow_left
              span Back

      v-layout(row)
        v-flex(xs3)
          title-bar Compose A Text
        v-flex.pt-3(text-xs-right xs6 offset-xs6)
          v-btn(flat @click="templatePickerVisible = true") Choose a Template
          v-btn(flat @click="showClearConfirmationDialog") Clear All
      v-card
        //- The actual text editor
        text-recipients
        sms-rates-info(v-if="showBulkTextsWarning")
        text-subject-body(
          v-model="desiredTemplate"
          @click:choose-template="templatePickerVisible = true"
          @click:clear-fields="showClearConfirmationDialog"
        )
        text-sending-options(
          recurrenceInputSize="xs6"
          @click:send="clearAll"
          isBulkTexts
        )
      template-picker(
        communicationType="Text"
        v-model="templatePickerVisible"
        :useSlideDrawer="true"
        :template="desiredTemplate"
        @change="onTemplateChange"
      )
</template>
<script>
import { createNamespacedHelpers } from 'vuex';
import TitleBar from '@/Core/components/TitleBar';
import CommunicationsLayout from '@/Communications/Layout';
import SlideDrawer from '@/Core/components/SlideDrawer';
import TemplatePicker from '@/Campaigns/components/TemplatePicker';
import LiondeskDialog from '@/Core/components/LiondeskDialog';
import ConfirmLeaveModal from '@/Core/mixins/confirmLeaveModal';
import GoBackNavigation from '@/Contacts/mixins/goBackNavigation';
import ProcessingActionModal from '@/Contacts/components/actions/ProcessingActionModal';
import TextRecipients from './components/TextRecipients';
import TextSubjectBody from './components/TextSubjectBody';
import TextSendingOptions from './components/TextSendingOptions';
import SmsRatesInfo from './components/SmsRatesInfo.vue';
import { LdFeatures } from 'lion-vue';

const {
  mapState: mapTemplatesState,
  mapActions: mapTemplatesActions,
  mapGetters: mapTemplateGetters,
} = createNamespacedHelpers('templates');

const {
  mapActions: mapRecipientsActions,
  mapGetters: mapRecipientGetters,
  mapState: mapRecipientsState,
} = createNamespacedHelpers('recipients');

const {
  mapState: mapConversationsState,
  mapActions: mapConversationsActions,
} = createNamespacedHelpers(
  'conversations',
);

const { mapGetters: mapUserInformationGetters } = createNamespacedHelpers(
  'userInformation',
);

const { 
  mapState: mapUserSmsErrorRatesState,
  mapActions: mapUserSmsErrorRatesActions,
} = createNamespacedHelpers(
  'userSmsErrorRates',
);

export default {
  components: {
    TitleBar,
    CommunicationsLayout,
    SlideDrawer,
    TemplatePicker,
    LiondeskDialog,
    TextRecipients,
    TextSubjectBody,
    TextSendingOptions,
    ProcessingActionModal,
    SmsRatesInfo,
  },

  mixins: [ConfirmLeaveModal, GoBackNavigation],

  data() {
    return {
      templatePickerVisible: false,
      clearConfirmationDialog: false,
      // The desired template we want to show in the composer
      desiredTemplate: {},
    };
  },

  computed: {
    ...mapTemplatesState([
      'isEditing',
      'templatesError',
      'templatesErrorMessage',
      'templatesSuccess',
      'templatesSuccessMessage',
    ]),
    ...mapRecipientGetters(['isRecipientsEmpty']),
    ...mapRecipientsState(['toSelected']),
    ...mapTemplateGetters(['isSubjectBodyEmpty']),
    ...mapConversationsState([
      'conversationsError',
      'conversationsErrorMessage',
      'conversationsSuccess',
      'conversationsSuccessMessage',
      'sendingTextInProgress',
      'textsSent',
      'has10dlcRegister',
      'isRegistration10dlcIncomplete',
    ]),
    ...mapUserInformationGetters(['userHasFeatureFlag', 'hasAccessToFeature']),
    ...mapUserSmsErrorRatesState(['isPermaBlocked']),
    // Indicates thre is user-inputted data
    hasUserInput() {
      return !this.isRecipientsEmpty || !this.isSubjectBodyEmpty;
    },
    showBulkTextsWarning() {
      return this.hasAccessToFeature(LdFeatures.BulkTexts) && !this.isPermaBlocked;
    },
  },
  async created() {
    this.clearErrors();
    this.clearSuccess();
    this.getIsUserBlocked();
    this.getRates();
    if(!this.has10dlcRegister || this.isRegistration10dlcIncomplete) {
      await this.checkFor10dlcRegister();
    }
  },

  // Before route leave to add modal for user confirmation
  beforeRouteLeave(to, from, next) {
    if (!this.hasUserInput) {
      // If there is not user-inputted data, perform the intended action
      return next();
    }
    // Opens the confirmation modal if the user
    this.confirmLeaveModal_open(next);
  },

  methods: {
    ...mapTemplatesActions(['setEditing', 'clearErrors', 'clearSuccess']),
    ...mapRecipientsActions(['setToSelected']),
    ...mapConversationsActions(['checkFor10dlcRegister']),
    ...mapUserSmsErrorRatesActions(['getIsUserBlocked', 'getRates']),
    /*
     * This function resets every field to their initial values.
     */
    clearAll() {
      // Clears the selected template
      this.desiredTemplate = {};

      // Clears the selected contacts
      this.setToSelected([]);

      // Sets the editing status to false
      this.setEditing(false);

      // CLose the confirmation dialog
      this.clearConfirmationDialog = false;
    },
    showClearConfirmationDialog() {
      this.clearConfirmationDialog = true;
    },
    onTemplateChange(newValue) {
      this.desiredTemplate = Object.assign({}, newValue);
    },
  },
};
</script>
